import {
	RiAddLine,
	RiArrowRightSLine,
	RiDeleteBinLine,
	RiFileTextLine,
	RiSearchLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";
import type { WikiPageNode } from "@/routes/_app/wiki";
import { useWikiTree } from "@/routes/_app/wiki";

// --- Tree building ---

type TreeNode = WikiPageNode & { children: TreeNode[] };

function buildRecursiveTree(pages: WikiPageNode[]): TreeNode[] {
	const childMap = new Map<string, WikiPageNode[]>();

	for (const page of pages) {
		const parentKey = page.parentId ?? "__root__";
		if (!childMap.has(parentKey)) childMap.set(parentKey, []);
		childMap.get(parentKey)!.push(page);
	}

	function buildChildren(parentId: string): TreeNode[] {
		const children = childMap.get(parentId) ?? [];
		return children
			.sort((a, b) => a.position - b.position)
			.map((page) => ({
				...page,
				children: buildChildren(page.id),
			}));
	}

	return buildChildren("__root__");
}

// --- Drag & Drop types ---

type DropTarget =
	| {
			type: "on"; // drop ON a node (make it a child)
			targetId: string;
	  }
	| {
			type: "between"; // drop BETWEEN nodes (reorder)
			parentId: string | null; // null = root
			position: number;
	  };

// --- Props ---

interface WikiTreeProps {
	pages: WikiPageNode[];
	onNavigate: () => void;
}

export function WikiTree({ pages, onNavigate }: WikiTreeProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { expandedIds, toggleExpand } = useWikiTree();
	const { can } = usePermissions();
	const [creating, setCreating] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{
		id: string;
		title: string;
	} | null>(null);
	const [deleting, setDeleting] = useState(false);

	const canCreate = can("wiki", "create");
	const canMove = can("wiki", "move");
	const canDelete = can("wiki", "delete");

	// Drag state
	const [dragId, setDragId] = useState<string | null>(null);
	const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

	const tree = buildRecursiveTree(pages);

	// Get current pageId from URL params
	const params = useParams({ strict: false }) as { pageId?: string };
	const activePageId = params.pageId;

	async function handleNewPage() {
		setCreating(true);
		try {
			const { data, error } = await fetchClient.POST("/api/v1/wiki", {
				body: { title: "Untitled", status: "draft" },
			});
			if (error || !data) {
				toast.error("Failed to create page");
				return;
			}
			await queryClient.invalidateQueries({
				queryKey: ["get", "/api/v1/wiki"],
			});
			const newPage = data as { id: string };
			onNavigate();
			await navigate({ to: "/wiki/$pageId", params: { pageId: newPage.id } });
		} finally {
			setCreating(false);
		}
	}

	async function handleDelete() {
		if (!deleteTarget) return;
		setDeleting(true);
		try {
			const { error } = await fetchClient.DELETE("/api/v1/wiki/{id}", {
				params: { path: { id: deleteTarget.id } },
			});
			if (error) {
				toast.error("Failed to delete page");
				return;
			}
			await queryClient.invalidateQueries({
				queryKey: ["get", "/api/v1/wiki"],
			});
			toast.success("Page deleted");
			if (activePageId === deleteTarget.id) {
				await navigate({ to: "/wiki" });
			}
		} finally {
			setDeleting(false);
			setDeleteTarget(null);
		}
	}

	// --- Drag & drop handlers ---

	const handleDragStart = useCallback((pageId: string) => {
		setDragId(pageId);
	}, []);

	const handleDragEnd = useCallback(() => {
		setDragId(null);
		setDropTarget(null);
	}, []);

	const handleDrop = useCallback(
		async (target: DropTarget) => {
			if (!dragId) return;

			let parentId: string | null;
			let position: number;

			if (target.type === "on") {
				// Dropping ON a node: make dragged item the last child of target
				if (target.targetId === dragId) return; // can't drop on itself
				parentId = target.targetId;
				// Count existing children to get next position
				const existingChildren = pages.filter((p) => p.parentId === target.targetId);
				position = existingChildren.length;
			} else {
				parentId = target.parentId;
				position = target.position;
			}

			// Don't move to same spot
			const dragged = pages.find((p) => p.id === dragId);
			if (!dragged) return;
			if ((dragged.parentId ?? null) === parentId && dragged.position === position) return;

			setDragId(null);
			setDropTarget(null);

			const { error } = await fetchClient.POST("/api/v1/wiki/{id}/move", {
				params: { path: { id: dragId } },
				body: { parentId, position },
			});

			if (error) {
				toast.error("Failed to move page");
				return;
			}

			await queryClient.invalidateQueries({
				queryKey: ["get", "/api/v1/wiki"],
			});
		},
		[dragId, pages, queryClient],
	);

	// Check if a node is a descendant of another (prevent circular drops)
	const isDescendant = useCallback(
		(nodeId: string, potentialAncestorId: string): boolean => {
			const pageMap = new Map(pages.map((p) => [p.id, p]));
			let current = pageMap.get(nodeId);
			while (current) {
				if (current.id === potentialAncestorId) return true;
				current = current.parentId ? pageMap.get(current.parentId) : undefined;
			}
			return false;
		},
		[pages],
	);

	return (
		<div className="flex h-full flex-col">
			{/* Search shortcut hint */}
			<div className="p-3 pb-0">
				<button
					type="button"
					className="flex w-full items-center gap-2 rounded-md border bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted transition-colors"
					onClick={() => {
						window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
					}}
				>
					<RiSearchLine className="size-3.5" />
					<span>Search...</span>
					<kbd className="ml-auto rounded border bg-background px-1 py-0.5 text-[10px] font-mono">
						{"\u2318"}K
					</kbd>
				</button>
			</div>

			{/* Tree */}
			<div className="flex-1 overflow-y-auto p-3">
				{tree.length === 0 ? (
					<p className="py-8 text-center text-xs text-muted-foreground">No pages yet</p>
				) : (
					<div className="space-y-0.5">
						{/* Drop zone at very top (before first root item) */}
						<DropZone
							dragId={dragId}
							target={{ type: "between", parentId: null, position: 0 }}
							dropTarget={dropTarget}
							onDropTargetChange={setDropTarget}
							onDrop={handleDrop}
							depth={0}
						/>
						{tree.map((node, index) => (
							<TreeItem
								key={node.id}
								node={node}
								depth={0}
								index={index}
								siblingCount={tree.length}
								activePageId={activePageId}
								expandedIds={expandedIds}
								toggleExpand={toggleExpand}
								onNavigate={onNavigate}
								onDelete={(id, title) => setDeleteTarget({ id, title })}
								dragId={dragId}
								dropTarget={dropTarget}
								onDragStart={handleDragStart}
								onDragEnd={handleDragEnd}
								onDropTargetChange={setDropTarget}
								onDrop={handleDrop}
								isDescendant={isDescendant}
								canMove={canMove}
								canDelete={canDelete}
							/>
						))}
					</div>
				)}
			</div>

			{/* New page button */}
			{canCreate && (
				<div className="border-t p-3">
					<Button
						variant="ghost"
						size="sm"
						className="w-full justify-start text-muted-foreground"
						onClick={handleNewPage}
						disabled={creating}
					>
						<RiAddLine className="mr-2 size-4" />
						{creating ? "Creating..." : "New page"}
					</Button>
				</div>
			)}

			{/* Delete confirmation dialog */}
			<Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete page?</DialogTitle>
						<DialogDescription>
							This will permanently delete &ldquo;{deleteTarget?.title}&rdquo;. This action cannot
							be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteTarget(null)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete} disabled={deleting}>
							{deleting ? "Deleting..." : "Delete"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

// --- Drop Zone (between items) ---

interface DropZoneProps {
	dragId: string | null;
	target: DropTarget & { type: "between" };
	dropTarget: DropTarget | null;
	onDropTargetChange: (target: DropTarget | null) => void;
	onDrop: (target: DropTarget) => void;
	depth: number;
}

function DropZone({
	dragId,
	target,
	dropTarget,
	onDropTargetChange,
	onDrop,
	depth,
}: DropZoneProps) {
	if (!dragId) return null;

	const isActive =
		dropTarget?.type === "between" &&
		dropTarget.parentId === target.parentId &&
		dropTarget.position === target.position;

	return (
		<div
			className="relative h-1 group"
			style={{ paddingLeft: `${depth * 16 + 8}px` }}
			onDragOver={(e) => {
				e.preventDefault();
				e.dataTransfer.dropEffect = "move";
				onDropTargetChange(target);
			}}
			onDragLeave={() => onDropTargetChange(null)}
			onDrop={(e) => {
				e.preventDefault();
				onDrop(target);
			}}
		>
			<div
				className={cn(
					"absolute inset-x-0 h-0.5 rounded-full transition-colors",
					isActive ? "bg-primary" : "bg-transparent",
				)}
				style={{ marginLeft: `${depth * 16 + 8}px` }}
			/>
		</div>
	);
}

// --- Tree Item ---

interface TreeItemProps {
	node: TreeNode;
	depth: number;
	index: number;
	siblingCount: number;
	activePageId?: string;
	expandedIds: Set<string>;
	toggleExpand: (id: string) => void;
	onNavigate: () => void;
	onDelete: (id: string, title: string) => void;
	dragId: string | null;
	dropTarget: DropTarget | null;
	onDragStart: (id: string) => void;
	onDragEnd: () => void;
	onDropTargetChange: (target: DropTarget | null) => void;
	onDrop: (target: DropTarget) => void;
	isDescendant: (nodeId: string, ancestorId: string) => boolean;
	canMove: boolean;
	canDelete: boolean;
}

function TreeItem({
	node,
	depth,
	index,
	siblingCount,
	activePageId,
	expandedIds,
	toggleExpand,
	onNavigate,
	onDelete,
	dragId,
	dropTarget,
	onDragStart,
	onDragEnd,
	onDropTargetChange,
	onDrop,
	isDescendant,
	canMove,
	canDelete,
}: TreeItemProps) {
	const navigate = useNavigate();
	const dragOverRef = useRef<"top" | "center" | "bottom" | null>(null);
	const isExpanded = expandedIds.has(node.id);
	const isActive = activePageId === node.id;
	const hasChildren = node.children.length > 0;
	const isDraft = node.status === "draft";
	const isDragging = dragId === node.id;
	const parentId = node.parentId ?? null;

	// Can this node accept a drop? (not itself, not a descendant of dragged)
	const canDropOn = dragId !== null && dragId !== node.id && !isDescendant(node.id, dragId);
	const isDropOnActive = dropTarget?.type === "on" && dropTarget.targetId === node.id;

	function handleDragOver(e: React.DragEvent) {
		e.preventDefault();
		if (!canDropOn) return;

		const rect = e.currentTarget.getBoundingClientRect();
		const y = e.clientY - rect.top;
		const third = rect.height / 3;

		if (y < third) {
			// Top third — drop before this item
			dragOverRef.current = "top";
			onDropTargetChange({ type: "between", parentId, position: index });
		} else if (y > third * 2) {
			// Bottom third — drop after this item
			dragOverRef.current = "bottom";
			// If expanded and has children, drop as first child instead
			if (isExpanded && hasChildren) {
				onDropTargetChange({ type: "between", parentId: node.id, position: 0 });
			} else {
				onDropTargetChange({ type: "between", parentId, position: index + 1 });
			}
		} else {
			// Center — drop ON this item (make child)
			dragOverRef.current = "center";
			onDropTargetChange({ type: "on", targetId: node.id });
		}

		e.dataTransfer.dropEffect = "move";
	}

	return (
		<div className={cn(isDragging && "opacity-40")}>
			<DropdownMenu>
				<div
					draggable={canMove}
					onDragStart={(e) => {
						e.dataTransfer.effectAllowed = "move";
						e.dataTransfer.setData("text/plain", node.id);
						// Use setTimeout so the dragged element renders before we set state
						setTimeout(() => onDragStart(node.id), 0);
					}}
					onDragEnd={onDragEnd}
					onDragOver={handleDragOver}
					onDragLeave={() => {
						dragOverRef.current = null;
						onDropTargetChange(null);
					}}
					onDrop={(e) => {
						e.preventDefault();
						if (dropTarget) onDrop(dropTarget);
					}}
					className={cn(
						"group flex items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors",
						canMove ? "cursor-grab" : "cursor-default",
						isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted/70",
						isDraft && "opacity-60 italic",
						isDropOnActive && "ring-2 ring-primary ring-inset rounded-md",
					)}
					style={{ paddingLeft: `${depth * 16 + 8}px` }}
					onClick={() => {
						onNavigate();
						navigate({ to: "/wiki/$pageId", params: { pageId: node.id } });
					}}
				>
					{/* Expand/collapse arrow */}
					{hasChildren ? (
						<button
							type="button"
							className="shrink-0 rounded p-0.5 hover:bg-muted"
							onClick={(e) => {
								e.stopPropagation();
								toggleExpand(node.id);
							}}
						>
							<RiArrowRightSLine
								className={cn(
									"size-3.5 transition-transform duration-150",
									isExpanded && "rotate-90",
								)}
							/>
						</button>
					) : (
						<span className="w-[18px] shrink-0" />
					)}

					{/* Icon */}
					{node.icon ? (
						<span className="shrink-0 text-sm">{node.icon}</span>
					) : (
						<RiFileTextLine className="size-3.5 shrink-0 text-muted-foreground" />
					)}

					{/* Title */}
					<span className="truncate flex-1">{node.title}</span>

					{/* Context menu trigger (three-dot on hover) */}
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className="shrink-0 rounded p-0.5 opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity"
							onClick={(e) => e.stopPropagation()}
						>
							<span className="text-xs text-muted-foreground">&#8226;&#8226;&#8226;</span>
						</button>
					</DropdownMenuTrigger>
				</div>

				{canDelete && (
					<DropdownMenuContent align="start" sideOffset={4}>
						<DropdownMenuGroup>
							<DropdownMenuItem
								className="text-destructive"
								onClick={() => onDelete(node.id, node.title)}
							>
								<RiDeleteBinLine className="size-4" />
								Delete
							</DropdownMenuItem>
						</DropdownMenuGroup>
					</DropdownMenuContent>
				)}
			</DropdownMenu>

			{/* Children */}
			{isExpanded && hasChildren && (
				<div>
					{node.children.map((child, childIndex) => (
						<TreeItem
							key={child.id}
							node={child}
							depth={depth + 1}
							index={childIndex}
							siblingCount={node.children.length}
							activePageId={activePageId}
							expandedIds={expandedIds}
							toggleExpand={toggleExpand}
							onNavigate={onNavigate}
							onDelete={onDelete}
							dragId={dragId}
							dropTarget={dropTarget}
							onDragStart={onDragStart}
							onDragEnd={onDragEnd}
							onDropTargetChange={onDropTargetChange}
							onDrop={onDrop}
							isDescendant={isDescendant}
							canMove={canMove}
							canDelete={canDelete}
						/>
					))}
				</div>
			)}

			{/* Drop zone after last item in a group */}
			{index === siblingCount - 1 && (
				<DropZone
					dragId={dragId}
					target={{ type: "between", parentId, position: index + 1 }}
					dropTarget={dropTarget}
					onDropTargetChange={onDropTargetChange}
					onDrop={onDrop}
					depth={depth}
				/>
			)}
		</div>
	);
}
